import { IkeUserRole } from '@ikegps/core-api';
import { action, observable, makeObservable } from 'mobx';
import { FabButton } from '../../components/button/fab.button';
import { ImageButton } from '../../components/button/image.button';
import { MaterialIcon } from '../../components/icon/icons';
import { MenuComponent } from '../../components/menu/menu';
import { MenuItemComponent } from '../../components/menu/menu.item';
import { IMithrilStateComponent } from '../../components/mithril.component';
import { Spinner } from '../../components/spinner/spinner';
import { Table } from '../../components/table/table';
import { addStyles } from '../../components/theme';
import { T, Translate } from '../../i18n/index';
import { m } from '../../mithril';
import type { MithrilVNode } from '../../mithril';
import { Services } from '../../services';
import type { DepartmentUserProfile } from '../../services/user.profile.service';
import { userDisplayName } from '../../util/userDisplayName';
import { AccountsInviteComponent } from './accounts.confirm.invite';
import { AccountsConfirmRemove } from './accounts.confirm.remove';
import { AccountsConfirmRole } from './accounts.confirm.role';
import type { AccountsCtrl } from './accounts.ctrl';
import { CSS, Style } from './accounts.style';

addStyles(Style);

export class AccountsComponent extends IMithrilStateComponent {
	ctrl: AccountsCtrl;
	roleMenu: MenuComponent;
	@observable selectedUser: DepartmentUserProfile | null = null;

	constructor(ctrl: AccountsCtrl) {
		super();

		makeObservable(this);

		this.ctrl = ctrl;

		this.roleMenu = new MenuComponent({
			items: () => [
				{
					view: MenuItemComponent.view(Translate(T.ROLE_ADMIN)),
					action: () => this.confirmUserRoleChange(IkeUserRole.ROOT),
					visible: true,
					disabled: this.selectedUser && this.selectedUser.userLevel === IkeUserRole.ROOT,
					selected: this.selectedUser && this.selectedUser.userLevel === IkeUserRole.ROOT,
				},
				{
					view: MenuItemComponent.view(Translate(T.ROLE_USER)),
					action: () => this.confirmUserRoleChange(IkeUserRole.USER),
					visible: true,
					divider: this.ctrl.haveExtraRoles(),
					disabled: this.selectedUser && this.selectedUser.userLevel === IkeUserRole.USER,
					selected: this.selectedUser && this.selectedUser.userLevel === IkeUserRole.USER,
				},
				{
					view: MenuItemComponent.view(Translate(T.ROLE_VIEWER)),
					action: () => this.confirmUserRoleChange(IkeUserRole.VIEWER),
					visible: this.ctrl.haveExtraRoles(),
					disabled: this.selectedUser && this.selectedUser.userLevel === IkeUserRole.VIEWER,
					selected: this.selectedUser && this.selectedUser.userLevel === IkeUserRole.VIEWER,
				},
				{
					view: MenuItemComponent.view(Translate(T.ROLE_EDITOR)),
					action: () => this.confirmUserRoleChange(IkeUserRole.EDITOR),
					visible: this.ctrl.haveExtraRoles(),
					disabled: this.selectedUser && this.selectedUser.userLevel === IkeUserRole.EDITOR,
					selected: this.selectedUser && this.selectedUser.userLevel === IkeUserRole.EDITOR,
				},
				{
					view: MenuItemComponent.view(Translate(T.ROLE_DOWNLOADER)),
					action: () => this.confirmUserRoleChange(IkeUserRole.DOWNLOADER),
					visible: this.ctrl.haveExtraRoles(),
					disabled: this.selectedUser && this.selectedUser.userLevel === IkeUserRole.DOWNLOADER,
					selected: this.selectedUser && this.selectedUser.userLevel === IkeUserRole.DOWNLOADER,
				},
				{
					view: MenuItemComponent.view(Translate(T.ROLE_DATA_ADMIN)),
					action: () => this.confirmUserRoleChange(IkeUserRole.DATA_ADMIN),
					visible: this.ctrl.haveExtraRoles(),
					disabled: this.selectedUser && this.selectedUser.userLevel === IkeUserRole.DATA_ADMIN,
					selected: this.selectedUser && this.selectedUser.userLevel === IkeUserRole.DATA_ADMIN,
				},
			],
		});
	}

	get nodes(): MithrilVNode {
		return m('div', { className: CSS.container }, [this.viewUsers(), this.viewAddButton()]);
	}

	@action private setSelectedUser(user: DepartmentUserProfile) {
		this.selectedUser = user;
	}

	private confirmUserRoleChange(userRole: IkeUserRole) {
		const confirm = new AccountsConfirmRole(this.ctrl, this.selectedUser, userRole);
		confirm.open();
	}

	private viewAddButton(): MithrilVNode {
		if (!this.ctrl.isDepartmentAdmin) {
			return null;
		}
		return FabButton.view({
			title: T.ACCOUNTS_INVITE_TITLE,
			onclick: () => {
				const requestInviteComponent = new AccountsInviteComponent(this.ctrl);
				requestInviteComponent.open();
			},
		});
	}

	private viewUsers(): MithrilVNode {
		const headers = ['#', Translate(T.SIGNUP_EMAIL), Translate(T.ACCOUNTS_NAME), Translate(T.ACCOUNTS_ROLE)];
		const rows: MithrilVNode[][] = [];

		const departmentUsers = this.ctrl.users;
		for (let i = 0; i < departmentUsers.length; i++) {
			const user = departmentUsers[i];
			rows.push(this.viewUser(user, i));
		}

		return Table.view(Translate(T.ACCOUNTS_TITLE), headers, rows);
	}

	private viewUser(user: DepartmentUserProfile, index: number): MithrilVNode[] {
		const displayName = userDisplayName(user);
		return [
			m('span', { className: CSS.tableCell }, String(index + 1)),
			m('span', { className: CSS.tableCell }, user.username),
			m('span', { className: CSS.tableCellFaint }, displayName !== user.username ? displayName : ''),
			m('span', { className: CSS.changeRoleWrapper }, this.viewRoleButton(user)),
			this.ctrl.isDepartmentAdmin ? this.viewRoleActions(user) : null,
		];
	}

	private viewRole(user: DepartmentUserProfile): MithrilVNode {
		const userLevel = this.getUserLevel(user);
		const roleTranslateKey = Services.UserProfile.isDepartmentAdmin(user) ? T.ROLE_ADMIN : T.ROLE_USER;
		if (userLevel) {
			return m('span', {}, userLevel);
		}
		return m('span', {}, Translate(roleTranslateKey));
	}

	private viewRoleButton(user: DepartmentUserProfile): MithrilVNode | MithrilVNode[] {
		if (this.ctrl.isLoadingUser(user)) {
			return m('div', { className: CSS.tableCellRoleActions }, Spinner.view());
		}

		if (user.objectId === Services.User.current.objectId) {
			return [
				m(
					'button',
					{
						className: `${CSS.changeRole}  mdl-button mdl-js-button mdl-js-ripple-effect`,
						style: { 'font-size': '13px' },
						disabled: true,
					},
					this.viewRole(user)
				),
			];
		}

		if (!this.ctrl.isDepartmentAdmin) {
			return [
				m(
					'button',
					{
						className: `${CSS.changeRole}  mdl-button mdl-js-button mdl-js-ripple-effect`,
						style: { 'font-size': '13px', color: '#000000' },
						disabled: true,
					},
					this.viewRole(user)
				),
			];
		}

		return [
			m(
				'button',
				{
					className: `${CSS.changeRole}  mdl-button mdl-js-button mdl-js-ripple-effect`,
					style: { 'font-size': '13px' },
					onclick: () => {
						this.setSelectedUser(user);
						this.roleMenu.show();
					},
				},
				this.viewRole(user)
			),
			this.selectedUser && this.selectedUser.objectId === user.objectId ? this.roleMenu.view() : null,
		];
	}

	private getUserLevel(user: DepartmentUserProfile): string {
		if (Services.UserProfile.isDepartmentEditor(user)) {
			return Translate(T.ROLE_EDITOR);
		}

		if (Services.UserProfile.isDepartmentDownloader(user)) {
			return Translate(T.ROLE_DOWNLOADER);
		}

		if (Services.UserProfile.isDepartmentViewer(user)) {
			return Translate(T.ROLE_VIEWER);
		}

		if (Services.UserProfile.isDepartmentDataAdmin(user)) {
			return Translate(T.ROLE_DATA_ADMIN);
		}

		return null;
	}

	private viewRoleActions(user: DepartmentUserProfile): MithrilVNode {
		if (this.ctrl.isLoadingUser(user)) {
			return m('div', { className: CSS.tableCellRoleActions }, Spinner.view());
		}

		const role = user.role;
		return m('div', { className: `${CSS.tableCellRoleActions} ${CSS.tableCellGhost}` }, [
			this.ctrl.canRemove(role) ? this.viewRemoveButton(user) : null,
		]);
	}

	private viewRemoveButton(user: DepartmentUserProfile): MithrilVNode {
		return ImageButton.view(MaterialIcon.DELETE, {
			title: T.ACCOUNTS_REMOVE,
			onclick: () => {
				const confirm = new AccountsConfirmRemove(this.ctrl, user);
				confirm.open();
			},
		});
	}
}
